/**
 * What this conversation turned out to belong to, on the end of the sender
 * line.
 *
 * These three chips are the reason the mail screen matters beyond reading mail.
 * A message arrives as an address and a subject; what makes it useful is the
 * client it was filed under, the company the sender was matched to and the
 * campaign that caused the reply. They used to sit on a strip of their own
 * between the header and the first message - a full row of the reading pane
 * spent on three facts that are usually one short word each, and which were
 * being repeated a centimetre below the same name in the header.
 *
 * They are answers, not controls: setting the client is the circle upstairs,
 * and the matched company is the scanner's own doing. So each chip is a link to
 * the record it names and nothing else.
 */
import { Link } from "react-router-dom";
import { Building2, Megaphone, Users } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailThread } from "../../../../lib/mail";

/** Same name, ignoring case and the spaces around it. */
const sameName = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export function ThreadTags({ thread, campaignName }: { thread: MailThread; campaignName?: string }) {
  const t = useT();
  /**
   * The client, unless it is the matched company under another icon.
   *
   * Filing a scanned company as a client is the normal path through this
   * module, so the two names are the same string more often than not - and two
   * identical pills side by side read as a rendering bug, not as two facts. The
   * company chip is the one kept: it is what the operator asked to see here,
   * and the client is one press away on the circle above.
   */
  const client = thread.clientId && !sameName(thread.client?.name, thread.matchedCompanyName) ? thread.client : null;

  if (!client && !thread.matchedRegCode && !thread.campaignId) return null;

  return (
    <span className="crm-read-tags">
      {client ? (
        <Link to={`/clients/${thread.clientId}`} className="crm-chip" title={t("email.linkClient")}>
          <Users className="size-3 shrink-0" />
          <span className="crm-trunc">{client.name}</span>
        </Link>
      ) : null}

      {thread.matchedRegCode ? (
        <Link to={`/ariregister/${thread.matchedRegCode}`} className="crm-chip" title={t("email.matchedCompany")}>
          <Building2 className="size-3 shrink-0" />
          <span className="crm-trunc">{thread.matchedCompanyName ?? thread.matchedRegCode}</span>
        </Link>
      ) : null}

      {thread.campaignId ? (
        <Link
          to={`/marketing/campaigns/${thread.campaignId}`}
          className="crm-chip"
          title={t("email.openCampaign")}
          style={{
            background: "color-mix(in oklab, var(--color-primary-2) 13%, transparent)",
            color: "var(--color-primary-2)",
          }}
        >
          <Megaphone className="size-3 shrink-0" />
          <span className="crm-trunc">{campaignName ?? t("email.fromCampaign")}</span>
        </Link>
      ) : null}
    </span>
  );
}
